// =============================================================================
// The app as Procore renders it: an iframe inside Procore's own UI, either as
// a full-screen workspace launched from the Apps menu or as the 400-px side
// panel docked to the right of a tool.
//
// This is deliberately NOT the whole app in a frame. A side panel is a column
// barely wider than a phone, sitting next to a drawing or a document the user
// is already reading, and the question they have is about THAT project. So
// the panel resolves the Procore project to its counterpart here and opens
// chat on it - one surface, no navigation, no project picker. Anything that
// needs the full app (adding projects, importing, agents, reports) gets a link
// that opens a real tab, because it does not belong in a 400-px column.
//
// Two things make this page different from every other page, and both are
// forced by the frame:
//
//  - Sign-in must be a popup. Entra refuses to be framed, so the redirect
//    flow would navigate the panel to a page the browser blocks. auth.js
//    handles this; this page just calls signIn().
//  - The page must be allowed to be framed at all. Our Static Web App sends
//    Content-Security-Policy: frame-ancestors 'self' https://*.procore.com for
//    that reason. With the old X-Frame-Options: DENY, Procore showed nothing
//    but "refused to connect".
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { initAuth, isSignedIn, signIn, getStoredAccount } from '../services/auth';
import { fetchProjects, fetchFullProject, fetchTenantContext } from '../services/api';
import { getSelectedTenantId, setSelectedTenantId } from '@mobile-services/tenantSelection';
import { ProjectProvider } from '../ProjectContext';
import { getProjectId, getProjectName } from '@shared/targetHelpers';
import { Button, Loading, ErrorText, errorMessage } from '../components/ui';
import OrgPickerPage from './OrgPickerPage';
import ChatPage from './ChatPage';
import logo from '@assets/ydyai-logo.png';

// An identity with seats in more than one tenant has to say which one it is
// working in, or the API resolves to none and answers tenant_required. The
// main app asks on load; the panel has to ask too, and this is where it does.
//
// Remembering the answer matters more here than anywhere else in the app. A
// side panel's iframe reloads every time the user moves around Procore, so an
// in-memory choice (which is all tenantSelection keeps, deliberately) would
// put "Choose organisation" in front of them on every page they visit.
//
// sessionStorage, not localStorage, and stamped with the account that chose:
// the choice then dies with the browser tab, cannot outlive the MSAL session
// that lives in the same partition, and can never be handed to a different
// account signing in afterwards - which is the leak that made persisting this
// a bad idea in the first place.
const TENANT_CHOICE_KEY = 'ydyai.embedded.tenant';

function accountKey() {
  const account = getStoredAccount();
  return account?.oid || account?.email || '';
}

function rememberedTenantId(key) {
  if (!key) return '';
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(TENANT_CHOICE_KEY) || 'null');
    return saved && saved.account === key ? String(saved.tenantId || '') : '';
  } catch {
    return '';
  }
}

function rememberTenantId(key, tenantId) {
  try {
    window.sessionStorage.setItem(TENANT_CHOICE_KEY, JSON.stringify({ account: key, tenantId }));
  } catch {
    /* a browser refusing site data has already failed sign-in; nothing to add */
  }
}

// Procore runs in several regions (app, us02, uk01, the sandboxes), and new
// ones appear. Matching the suffix covers them all without a list that goes
// stale, and still refuses anything that merely CONTAINS the name -
// "procore.com.evil.test" fails, which a substring check would not.
const PROCORE_HOST = /(^|\.)procore\.com$/i;

function isProcoreOrigin(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && PROCORE_HOST.test(url.hostname);
  } catch {
    return false;
  }
}

// Which Procore page is framing us. Needed as postMessage's targetOrigin:
// Procore's own guidance is to name the parent explicitly rather than "*", so
// the context message cannot be read by a page that merely happens to be
// framing us.
function parentOrigin() {
  const referrer = typeof document === 'undefined' ? '' : document.referrer;
  return referrer && isProcoreOrigin(referrer) ? new URL(referrer).origin : '';
}

// Context arrives by two routes, and which one depends on how the component
// was set up in the Developer Portal:
//
//  - URL parameter interpolation, available to both placements: the URL is
//    configured as .../embedded/procore?companyId={{procore.company.id}}&
//    projectId={{procore.project.id}}&projectName={{procore.project.name}}
//    and Procore substitutes the values per install.
//  - postMessage, side panel only: we send {type:'initialize'} to the parent
//    and it answers with a 'setup' message carrying company, project, the
//    view key and the id of the record on screen.
//
// Both are read. The query string is there on first paint, so the panel can
// get on with loading; the message, when it comes, is authoritative because it
// also tells us which record the user is looking at.
function useProcoreContext() {
  const [context, setContext] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pick = (...names) => {
      for (const name of names) {
        const value = params.get(name);
        if (value) return String(value).trim();
      }
      return '';
    };
    return {
      companyId: pick('companyId', 'company_id', 'procoreCompanyId'),
      projectId: pick('projectId', 'project_id', 'procoreProjectId'),
      projectName: pick('projectName', 'project_name'),
      view: '',
      resourceId: '',
    };
  });

  useEffect(() => {
    const origin = parentOrigin();
    const onMessage = (event) => {
      // Any page can postMessage to us, so the origin is checked before the
      // payload is looked at - and against the actual parent when we know it.
      if (!isProcoreOrigin(event.origin)) return;
      if (origin && event.origin !== origin) return;
      const data = event.data;
      if (!data || data.type !== 'setup') return;
      const incoming = data.context || {};
      setContext((previous) => ({
        companyId: String(incoming.company_id || previous.companyId || ''),
        projectId: String(incoming.project_id || previous.projectId || ''),
        projectName: previous.projectName,
        view: String(incoming.view || ''),
        resourceId: incoming.id == null ? '' : String(incoming.id),
      }));
    };
    window.addEventListener('message', onMessage);
    // Procore only sends the setup message in reply, so this handshake is what
    // starts it. Full-screen components get nothing back, which is why the
    // query string is configured as well.
    if (origin) window.parent.postMessage({ type: 'initialize' }, origin);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return context;
}

// The Procore project id is what joins the two sides, and it is already on
// every project we imported from Procore. Name is the fallback for a project
// that was created here first and happens to match - worth trying, because
// the alternative is a dead panel.
function matchProject(projects, context) {
  const wantedId = String(context.projectId || '').trim();
  if (wantedId) {
    const byId = projects.find((p) => String(p?.procoreProjectId || '').trim() === wantedId);
    if (byId) return byId;
  }
  const wantedName = String(context.projectName || '').trim().toLowerCase();
  if (wantedName) {
    const byName = projects.find(
      (p) => String(getProjectName(p) || '').trim().toLowerCase() === wantedName
    );
    if (byName) return byName;
  }
  return null;
}

function Shell({ children }) {
  return (
    <div className="embedded">
      <header className="embedded-bar">
        <img className="embedded-logo" src={logo} alt="your data your ai" />
      </header>
      <main className="embedded-body">{children}</main>
    </div>
  );
}

// A panel can't send the user off to another page in our app - it would load
// the whole app into a 400-px column - so anything that needs the full UI
// opens a tab instead.
function OpenInApp({ path = '/projects', children }) {
  return (
    <a className="btn secondary compact" href={path} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

export default function EmbeddedProcorePage() {
  const context = useProcoreContext();
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [needsOrg, setNeedsOrg] = useState(false);
  const [tenantReady, setTenantReady] = useState(false);
  const [projects, setProjects] = useState(null);
  const [project, setProject] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    initAuth()
      .then(() => setSignedIn(isSignedIn()))
      .catch(() => setSignedIn(false))
      .finally(() => setReady(true));
  }, []);

  const onSignIn = useCallback(async () => {
    setError('');
    setSigningIn(true);
    try {
      await signIn();
      setSignedIn(isSignedIn());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSigningIn(false);
    }
  }, []);

  // Which tenant we are in, settled before anything is fetched - every call
  // below it would fail with tenant_required otherwise.
  useEffect(() => {
    if (!signedIn) return undefined;
    let live = true;
    const remembered = rememberedTenantId(accountKey());
    if (remembered && !getSelectedTenantId()) setSelectedTenantId(remembered);
    fetchTenantContext()
      .then((ctx) => {
        if (!live) return;
        if (ctx?.mode === 'needs_selection') setNeedsOrg(true);
        else setTenantReady(true);
      })
      .catch((err) => { if (live) setError(errorMessage(err)); });
    return () => { live = false; };
  }, [signedIn]);

  const onOrgPicked = useCallback(() => {
    rememberTenantId(accountKey(), getSelectedTenantId());
    setNeedsOrg(false);
    setTenantReady(true);
  }, []);

  useEffect(() => {
    if (!tenantReady) return undefined;
    let live = true;
    fetchProjects()
      .then((list) => { if (live) setProjects(Array.isArray(list) ? list : []); })
      .catch((err) => { if (live) setError(errorMessage(err)); });
    return () => { live = false; };
  }, [tenantReady]);

  const matched = useMemo(
    () => (projects ? matchProject(projects, context) : null),
    [projects, context]
  );

  // The same contract ProjectShell gives every project page: the full project,
  // loaded once, provided through ProjectProvider so ChatPage needs no
  // changes to run in here.
  const projectId = matched ? getProjectId(matched) : '';
  const refreshProject = useCallback(async () => {
    if (!projectId) return;
    try {
      setProject(await fetchFullProject(projectId));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [projectId]);

  useEffect(() => { refreshProject(); }, [refreshProject]);

  if (!ready) return <Shell><Loading text="Signing in…" /></Shell>;

  if (!signedIn) {
    return (
      <Shell>
        <p className="muted">Sign in to ask questions about this project's documents.</p>
        <ErrorText>{error}</ErrorText>
        <Button onClick={onSignIn} disabled={signingIn} block>
          {signingIn ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="card-hint">Sign-in opens in a separate window, because Microsoft will not run inside Procore's panel.</p>
      </Shell>
    );
  }

  if (needsOrg) return <Shell><OrgPickerPage onPicked={onOrgPicked} /></Shell>;

  if (error && !projects) return <Shell><ErrorText>{error}</ErrorText></Shell>;
  if (!tenantReady) return <Shell><Loading text="Loading…" /></Shell>;
  if (!projects) return <Shell><Loading text="Loading projects…" /></Shell>;

  if (!context.projectId && !context.projectName) {
    return (
      <Shell>
        <p>This panel needs to know which Procore project it is open on, and Procore did not pass one.</p>
        <p className="card-hint">
          In the Developer Portal, add <code>?companyId={'{{procore.company.id}}'}&amp;projectId={'{{procore.project.id}}'}</code> to
          the component's URL.
        </p>
      </Shell>
    );
  }

  if (!matched) {
    return (
      <Shell>
        <p>
          {context.projectName ? `"${context.projectName}" has` : 'This project has'} not been added to your
          workspace yet, so there are no documents to answer from.
        </p>
        <p className="card-hint">Add it from Integrations, then reopen this panel.</p>
        <OpenInApp path="/connections">Open integrations</OpenInApp>
      </Shell>
    );
  }

  if (!project) return <Shell><Loading text="Loading project…" /></Shell>;

  return (
    <Shell>
      <div className="embedded-project">
        <span className="embedded-project-name">{getProjectName(project) || 'Project'}</span>
        <OpenInApp path={`/projects/${encodeURIComponent(projectId)}`}>Open full app</OpenInApp>
      </div>
      <ErrorText>{error}</ErrorText>
      <ProjectProvider project={project} refreshProject={refreshProject}>
        <ChatPage />
      </ProjectProvider>
    </Shell>
  );
}
